import { useEffect, useRef } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@tankef/api-client';
import { useBatchTransferActivityStore, useCurrentUser } from '@tankef/core-react';

const settledStatuses = new Set([
  'validated',
  'completed',
  'partially_completed',
  'failed',
  'cancelled',
  'validation_failed',
]);

/** Unknown server states keep recovery active and never authorize a new action. */
export function isBatchTransferInFlight(status: string) {
  return !settledStatuses.has(status);
}

export function useBatchTransfers(page: number, enabled = true) {
  const user = useCurrentUser();
  return useQuery({
    queryKey: ['batch-transfers', 'list', user?.id, page],
    queryFn: () => clientApi.batchTransfers({ page, per_page: 20 }),
    enabled,
    refetchInterval: query =>
      query.state.data?.items.some(batch => isBatchTransferInFlight(batch.status)) ? 5000 : false,
  });
}

export function useBatchTransfer(id: string | undefined) {
  const user = useCurrentUser();
  const queued = useBatchTransferActivityStore(state =>
    user?.id && id ? state.byUser[user.id]?.[id]?.queued : undefined
  );
  const receipt = useRef<{
    snapshot: Awaited<ReturnType<typeof clientApi.batchTransfer>>;
    revision: number | undefined;
  }>();
  const query = useQuery({
    queryKey: ['batch-transfers', id, 'detail', user?.id],
    queryFn: async () => {
      const userId = user?.id;
      const batchId = id as string;
      const requestRevision = userId
        ? useBatchTransferActivityStore.getState().byUser[userId]?.[batchId]?.queued?.revision
        : undefined;
      const snapshot = await clientApi.batchTransfer(batchId);
      receipt.current = { snapshot, revision: requestRevision };
      return snapshot;
    },
    enabled: Boolean(id),
    refetchInterval: query =>
      queued || (query.state.data && isBatchTransferInFlight(query.state.data.status))
        ? 5000
        : false,
  });
  useEffect(() => {
    const received = receipt.current;
    if (!user?.id || !id || !query.data || !received) return;
    const snapshot = query.data;
    if (
      snapshot.status !== received.snapshot.status ||
      snapshot.completed_at !== received.snapshot.completed_at ||
      snapshot.processed_count !== received.snapshot.processed_count ||
      snapshot.succeeded_count !== received.snapshot.succeeded_count ||
      snapshot.failed_count !== received.snapshot.failed_count
    )
      return;
    useBatchTransferActivityStore.getState().observe(user.id, id, snapshot, received.revision);
  }, [id, user?.id, query.data, query.dataUpdatedAt]);
  return query;
}

export function useBatchTransferResults(
  id: string | undefined,
  enabled: boolean,
  page: number,
  polling: boolean
) {
  const user = useCurrentUser();
  return useQuery({
    queryKey: ['batch-transfers', id, 'results', user?.id, page],
    queryFn: () => clientApi.batchTransferResults(id as string, { page, per_page: 100 }),
    enabled: Boolean(id) && enabled,
    refetchInterval: polling ? 5000 : false,
  });
}

export function useCreateBatchTransfer() {
  const requestKeys = useRef(new WeakMap<File, Map<string, string>>());
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      file: File;
      sourceAccountId: string;
      onProgress?: (progress: number) => void;
    }) => {
      let keys = requestKeys.current.get(input.file);
      if (!keys) {
        keys = new Map();
        requestKeys.current.set(input.file, keys);
      }
      const scope = `${user?.id ?? 'session'}:${input.sourceAccountId}`;
      const key = keys.get(scope) ?? crypto.randomUUID();
      keys.set(scope, key);
      return clientApi.createBatchTransfer(
        input.file,
        input.sourceAccountId,
        input.onProgress,
        key
      );
    },
    onSuccess: async (batch, input) => {
      requestKeys.current
        .get(input.file)
        ?.delete(`${user?.id ?? 'session'}:${input.sourceAccountId}`);
      if (user?.id) useBatchTransferActivityStore.getState().track(user.id, [batch.id]);
      await queryClient.invalidateQueries({ queryKey: ['batch-transfers'] });
      queryClient.setQueryData(['batch-transfers', batch.id, 'detail', user?.id], batch);
    },
  });
}

export function useProcessBatchTransfer() {
  const requestKeys = useRef(new Map<string, string>());
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      const scope = `${user?.id ?? 'session'}:${id}`;
      const key = requestKeys.current.get(scope) ?? crypto.randomUUID();
      requestKeys.current.set(scope, key);
      return clientApi.processBatchTransfer(id, key);
    },
    onMutate: (id: string) => {
      const userId = user?.id;
      const baseline = queryClient.getQueryData<
        Awaited<ReturnType<typeof clientApi.batchTransfer>>
      >(['batch-transfers', id, 'detail', userId]);
      return { userId, baseline };
    },
    onSuccess: async (_, id, context) => {
      requestKeys.current.delete(`${context?.userId ?? 'session'}:${id}`);
      if (context?.userId) {
        useBatchTransferActivityStore.getState().queue(context.userId, id, context.baseline);
      }
      await queryClient.invalidateQueries({ queryKey: ['batch-transfers'] });
      await queryClient.invalidateQueries({ queryKey: ['batch-transfers', id] });
    },
  });
}

export function useRetryFailedBatchTransfer() {
  const requestKeys = useRef(new Map<string, string>());
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      const scope = `${user?.id ?? 'session'}:${id}`;
      const key = requestKeys.current.get(scope) ?? crypto.randomUUID();
      requestKeys.current.set(scope, key);
      return clientApi.retryFailedBatchTransfer(id, key);
    },
    onMutate: (id: string) => {
      const userId = user?.id;
      const baseline = queryClient.getQueryData<
        Awaited<ReturnType<typeof clientApi.batchTransfer>>
      >(['batch-transfers', id, 'detail', userId]);
      return { userId, baseline };
    },
    onSuccess: async (_, id, context) => {
      requestKeys.current.delete(`${context?.userId ?? 'session'}:${id}`);
      if (context?.userId) {
        useBatchTransferActivityStore.getState().queue(context.userId, id, context.baseline);
      }
      await queryClient.invalidateQueries({ queryKey: ['batch-transfers'] });
      await queryClient.invalidateQueries({ queryKey: ['batch-transfers', id] });
      await queryClient.invalidateQueries({ queryKey: ['batch-transfers', id, 'results'] });
    },
  });
}

export function useDownloadBatchTransferResults() {
  return useMutation({ mutationFn: (id: string) => clientApi.downloadBatchTransferResults(id) });
}

export function useActiveBatchTransfers(enabled: boolean, userId: string) {
  return useQuery({
    queryKey: ['batch-transfers', 'active', userId],
    enabled,
    queryFn: async () => {
      const first = await clientApi.batchTransfers({ page: 1, per_page: 100 });
      const items = [...first.items];
      // The current BE index ignores status filters, so discover active batches across its pages.
      for (let page = 2; page <= (first.meta?.total_pages ?? 1); page += 1) {
        const next = await clientApi.batchTransfers({ page, per_page: 100 });
        items.push(...next.items);
      }
      return Array.from(
        new Map(
          items
            .filter(batch => isBatchTransferInFlight(batch.status))
            .map(batch => [batch.id, batch])
        ).values()
      );
    },
    refetchInterval: 30000,
    retry: false,
  });
}
